import { useCallback, useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  EncryptionWarning,
  SettingField,
  TestButton,
  type SettingFieldData,
} from "../components/SettingField";
import { useI18n, serverError } from "../i18n";

interface SettingsPayload {
  encryptionReady: boolean;
  fields: (SettingFieldData & { group: string; secret: boolean })[];
}

/**
 * 金流設定
 * ---------------------------------------------------------------
 *
 * 兩套金流不是備援關係，是**市場決定的**：
 *   台灣 → 綠界（本地信用卡、ATM、超商代碼）
 *   其他市場 → PayPal
 *
 * 所以兩邊都要設好。只設一邊的話，另一個市場的使用者
 * 會在按下付款之後才看到錯誤 —— 那是最糟的失敗時機。
 */
export default function SettingsPayments() {
  const { t } = useI18n();
  const [data, setData] = useState<SettingsPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/settings/payments", { credentials: "same-origin" });
      const payload = await res.json().catch(() => null);
      if (!res.ok) throw new Error(serverError(payload, t, `HTTP ${res.status}`));
      setData(payload.data as SettingsPayload);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <p className="rounded-card nm-inset p-6 text-sm text-brand-300">{error}</p>;
  if (!data) return <div className="h-96 rounded-card shimmer" />;

  const field = (key: string) => data.fields.find((f) => f.key === key);
  const sandbox = field("payments_sandbox")?.preview !== "0";

  const put = (key: string, hint?: string, placeholder?: string) => {
    const f = field(key);
    if (!f) return null;
    return (
      <SettingField
        key={key}
        field={f}
        endpoint="/settings/payments"
        hint={hint}
        placeholder={placeholder}
        onSaved={load}
      />
    );
  };

  return (
    <div className="max-w-2xl space-y-5">
      {!data.encryptionReady && <EncryptionWarning service="payments" />}

      {/* 環境。放在最上面，因為它決定下面所有金鑰該填哪一組 */}
      <section className="rounded-card nm p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-ink">{t("sp.env")}</h2>
          <span
            className={
              "rounded-pill px-3 py-1 text-[11px] font-medium " +
              (sandbox ? "nm-sm text-ink-soft" : "bg-brand-600/26 text-brand-100")
            }
          >
            {sandbox ? t("sp.sandboxChip") : t("sp.liveChip")}
          </span>
        </div>

        <p className="mt-2 text-xs leading-relaxed text-ink-muted">
          {t("sp.envNote")}
        </p>

        {field("payments_sandbox") && (
          <div className="mt-4">
            <SettingField
              field={field("payments_sandbox")!}
              endpoint="/settings/payments"
              options={[
                { value: "1", label: t("sp.sandbox") },
                { value: "0", label: t("sp.live") },
              ]}
              onSaved={load}
            />
          </div>
        )}

        {!sandbox && (
          <p className="mt-3 flex items-start gap-2 rounded-xl nm-inset px-4 py-3 text-xs leading-relaxed text-brand-200">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
            {t("sp.liveWarn")}
          </p>
        )}
      </section>

      {/* PayPal */}
      <section className="rounded-card nm p-5">
        <h2 className="text-sm font-semibold text-ink">PayPal</h2>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
          {t("sp.paypalLede")}{" "}
          <a
            href="https://developer.paypal.com/dashboard/applications"
            target="_blank"
            rel="noreferrer noopener"
            className="text-brand-300 underline underline-offset-2"
          >
            PayPal Developer Dashboard
          </a>{" "}
          {t("sp.paypalLedeTail")}
        </p>

        <div className="mt-4 space-y-3">
          {put("paypal_client_id", undefined, "AX...")}
          {put("paypal_secret", t("sp.paypalSecretHint"), "EL...")}
          {put("paypal_webhook_id", t("sp.paypalWebhookHint"), "8XW...")}
        </div>

        <TestButton endpoint="/settings/payments/test/paypal" label={t("sp.testPaypal")} />
      </section>

      {/* 綠界 */}
      <section className="rounded-card nm p-5">
        <h2 className="text-sm font-semibold text-ink">{t("sp.ecpayName")}</h2>
        <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">
          {t("sp.ecpayLede")}
        </p>

        <div className="mt-4 space-y-3">
          {put("ecpay_merchant_id", undefined, "MS...")}
          {put("ecpay_hash_key", t("sp.ecpay32"), undefined)}
          {put("ecpay_hash_iv", t("sp.ecpay16"), undefined)}
        </div>

        <TestButton endpoint="/settings/payments/test/ecpay" label={t("sp.testEcpay")} />
        <p className="mt-2 text-[11px] leading-relaxed text-ink-muted">
          {t("sp.ecpayTestNote")}
        </p>
      </section>

      <section className="rounded-card nm-inset p-5">
        <h3 className="text-sm font-semibold text-ink">{t("sp.webhooks")}</h3>
        <p className="mt-2 text-xs leading-relaxed text-ink-soft">
          {t("sp.webhooksNote")}
        </p>
        <div className="mt-3 space-y-2 font-mono text-[11px] text-ink-soft">
          <p className="overflow-x-auto rounded-xl nm-sm px-4 py-2.5">
            https://colouredev.com/api/payments/webhook/paypal
          </p>
          <p className="overflow-x-auto rounded-xl nm-sm px-4 py-2.5">
            https://colouredev.com/api/payments/webhook/ecpay
          </p>
        </div>
      </section>
    </div>
  );
}
